import "./EnterCode.css";
import { Button, Card, Notification, rem, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { ContractResponseError } from "@spark/web";
import { IconX } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useEmbedMutation } from "../../lib/queries.ts";

const errorMessages = {
	404: "Code niet gevonden. Probeer het nog een keer.",
	410: "Code is verlopen. Vraag een nieuwe code aan.",
} as const;

type ErrorStatus = keyof typeof errorMessages;

function errorMessage(error: Error): string | null {
	const status = error instanceof ContractResponseError ? error.status : null;

	return status && status in errorMessages
		? errorMessages[status as ErrorStatus]
		: null;
}

/**
 * Renders the EnterCode component, allowing users to enter a code to access a Readiant document.
 * @returns {JSX.Element}
 */
export function EnterCode() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [searchParams] = useSearchParams();
	const [isValid, setIsValid] = useState(false);
	const [message, setMessage] = useState<string | null>(null);

	const xIcon = <IconX style={{ width: rem(20), height: rem(20) }} />;

	const form = useForm({
		initialValues: {
			code: searchParams.get("code") ?? "",
		},
		onValuesChange: (values) => {
			setIsValid(/^[\dA-Za-z]{5,22}$/.test(values.code));
		},
	});

	const mutation = useEmbedMutation();
	const submit = useCallback(
		(data: { code: string }) => {
			mutation.mutate(data, {
				onSuccess: (response) => {
					// Cache the result so Reader.tsx doesn't need to fetch again
					queryClient.setQueryData(["embed-code", data.code], response);
					navigate(`/${data.code}`);
				},
				onError: (error) => {
					form.setValues({ code: "" });
					setMessage(errorMessage(error));
				},
			});
		},
		[mutation, form, navigate, queryClient],
	);

	return (
		<Card className="enter-code" padding={0}>
			<div className="enter-code-form">
				<h1>Vul je code hier in:</h1>

				<div role="alert">
					{message && (
						<Notification
							className="enter-code-form-error"
							icon={xIcon}
							color="red"
							withCloseButton={false}
						>
							{message}
						</Notification>
					)}
				</div>

				<form onSubmit={form.onSubmit(submit)}>
					<TextInput
						type="text"
						{...form.getInputProps("code")}
						aria-label="Readiant Code"
					/>

					<Button type="submit" disabled={!isValid} size={"lg"} fullWidth>
						Ok
					</Button>
				</form>
			</div>
		</Card>
	);
}
